import { useEffect, useRef } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router'
import {
  authSessionAtom,
  authChallengeStepAtom,
  authResolvedAtom
} from 'shared/auth/state/authAtoms'
import { fetchUserMe, setUserMeCache } from 'shared/api/userMeQuery'
import { fetchCsrfToken } from 'shared/api/csrf'

// 利用規約・プライバシーポリシーは完全に認証不要なため、初回の userMe 取得を行わない。
const noAuthCheckPaths = ['/terms-of-use', '/privacy-policy']

/**
 * 初回マウント時に一度だけセッションを判定し、authAtoms を確定させる。
 * 旧 AuthProvider の loggedIn() + initialFetchDone 相当。遷移は route guard に委ねる。
 *
 * 判定は `GET /v1/users/me` で行う。api_session / SAML / 切替前の Cognito Cookie（legacy）の
 * いずれで認証されていても通るため、切替前にログインしていたユーザーもログアウトさせない。
 * `GET /v1/sessions` は未認証でも 204 を返すので判定に使わない（CSRF token の取得専用）。
 */
export const useAuthBootstrap = (): void => {
  const queryClient = useQueryClient()
  const { pathname } = useLocation()
  const resolved = useAtomValue(authResolvedAtom)

  const setSession = useSetAtom(authSessionAtom)
  const setChallengeStep = useSetAtom(authChallengeStepAtom)

  // StrictMode の二重実行や再レンダーで複数回走らないよう、実行済みフラグを持つ。
  const startedRef = useRef(false)

  useEffect(() => {
    if (resolved || startedRef.current) return
    startedRef.current = true

    void (async () => {
      // 認証不要ページはセッション判定をせず未認証のまま確定する(guard は authFreePaths で素通しする)。
      if (noAuthCheckPaths.includes(pathname)) {
        setSession('unauthenticated')
        return
      }

      try {
        const userMe = await fetchUserMe()
        setUserMeCache(queryClient, userMe)
        setChallengeStep(null)
        setSession('authenticated')
        void fetchCsrfToken()
      } catch {
        setUserMeCache(queryClient, null)
        setChallengeStep(null)
        setSession('unauthenticated')
      }
    })()
  }, [resolved, pathname, queryClient, setSession, setChallengeStep])
}
